import {
  ADDONS,
  ANNUAL_DISCOUNT,
  getAddon,
  getIndustry,
  getLocale,
  getPlan,
  planRank,
} from './catalog'
import type { AddonId, IndustryId, PlanId, Quote, QuoteInput, QuoteLine } from './types'

const CONVERSATION_BLOCK = 1000

/** Converts USD cents to the target locale's minor units, applying PPP then FX. */
function localise(usdCents: number, pppMultiplier: number, fxRate: number): number {
  return usdCents * pppMultiplier * fxRate
}

/**
 * Rounds to a display-friendly figure in the locale's major units, then returns minor units.
 * Prices that survive an FX conversion unrounded read as machine output, not as a price list.
 */
function roundToGranularity(minorAmount: number, roundTo: number, minorUnits: number): number {
  const granularity = roundTo * minorUnits
  if (granularity <= 0) return Math.round(minorAmount)
  return Math.round(minorAmount / granularity) * granularity
}

export function resolveRequiredAddons(industryId: IndustryId): AddonId[] {
  return ADDONS.filter((a) => a.requiredFor.includes(industryId)).map((a) => a.id)
}

export function isAddonAvailable(addonId: AddonId, planId: PlanId): boolean {
  const addon = getAddon(addonId)
  if (!addon) return false
  return planRank(planId) >= planRank(addon.minPlan)
}

/**
 * Normalises a partially-specified config into a valid one: clamps to plan limits,
 * forces industry-mandated addons in, and drops addons the plan cannot carry.
 */
export function normaliseInput(input: QuoteInput): { input: QuoteInput; warnings: string[] } {
  const warnings: string[] = []
  const plan = getPlan(input.planId)
  const industry = getIndustry(input.industryId)
  if (!plan || !industry) {
    return { input, warnings: ['Unknown plan or industry.'] }
  }

  let seats = Math.max(1, Math.floor(input.seats))
  let numbers = Math.max(1, Math.floor(input.numbers))
  let conversations = Math.max(0, Math.floor(input.conversations))

  if (plan.maxSeats !== null && seats > plan.maxSeats) {
    warnings.push(`${plan.name} caps at ${plan.maxSeats} seats. Reduced from ${seats}.`)
    seats = plan.maxSeats
  }
  if (plan.maxNumbers !== null && numbers > plan.maxNumbers) {
    warnings.push(`${plan.name} caps at ${plan.maxNumbers} numbers. Reduced from ${numbers}.`)
    numbers = plan.maxNumbers
  }

  const required = resolveRequiredAddons(input.industryId)
  const selected = new Set<AddonId>(input.addons)
  for (const id of required) {
    if (!selected.has(id)) {
      const addon = getAddon(id)
      warnings.push(`${addon?.name ?? id} is mandatory for ${industry.name} and has been added.`)
      selected.add(id)
    }
  }

  for (const id of [...selected]) {
    if (!isAddonAvailable(id, input.planId)) {
      const addon = getAddon(id)
      if (required.includes(id)) {
        warnings.push(
          `${addon?.name ?? id} requires the ${getPlan(addon!.minPlan)?.name} plan or above.`,
        )
      } else {
        selected.delete(id)
        warnings.push(`${addon?.name ?? id} is not available on ${plan.name} and was removed.`)
      }
    }
  }

  // Bundled addons are already paid for by the plan; keep them selected but they price at zero.
  for (const id of plan.bundledAddons) selected.add(id)

  return {
    input: { ...input, seats, numbers, conversations, addons: [...selected] },
    warnings,
  }
}

export function calculateQuote(rawInput: QuoteInput): Quote {
  const { input, warnings } = normaliseInput(rawInput)
  const plan = getPlan(input.planId)!
  const industry = getIndustry(input.industryId)!
  const locale = getLocale(input.localeId)!

  const toLocal = (usdCents: number) =>
    localise(usdCents, locale.pppMultiplier, locale.fxRate)

  const lines: QuoteLine[] = []

  // Base platform fee.
  const baseLocal = toLocal(plan.baseCents)
  lines.push({
    key: 'base',
    label: `${plan.name} platform fee`,
    detail: `${plan.includedSeats} seats, ${plan.includedNumbers} numbers, ${plan.includedConversations.toLocaleString()} conversations included`,
    amount: baseLocal,
    kind: 'base',
  })

  // Usage above the plan's included allowances.
  const extraSeats = Math.max(0, input.seats - plan.includedSeats)
  if (extraSeats > 0) {
    lines.push({
      key: 'seats',
      label: 'Additional seats',
      detail: `${extraSeats} × ${plan.name} seat rate`,
      amount: toLocal(extraSeats * plan.seatCents),
      kind: 'usage',
    })
  }

  const extraNumbers = Math.max(0, input.numbers - plan.includedNumbers)
  if (extraNumbers > 0) {
    lines.push({
      key: 'numbers',
      label: 'Additional WhatsApp numbers',
      detail: `${extraNumbers} × ${plan.name} number rate`,
      amount: toLocal(extraNumbers * plan.numberCents),
      kind: 'usage',
    })
  }

  const extraConversations = Math.max(0, input.conversations - plan.includedConversations)
  const extraBlocks = Math.ceil(extraConversations / CONVERSATION_BLOCK)
  if (extraBlocks > 0) {
    lines.push({
      key: 'conversations',
      label: 'Additional conversations',
      detail: `${extraBlocks} × 1,000 conversations`,
      amount: toLocal(extraBlocks * plan.conversationBlockCents),
      kind: 'usage',
    })
  }

  // Addons. Anything the plan bundles prices at zero but stays visible so the value is legible.
  const bundled = new Set(plan.bundledAddons)
  for (const addonId of input.addons) {
    const addon = getAddon(addonId)
    if (!addon) continue
    const isBundled = bundled.has(addonId)
    // An on-request addon is shown so the requirement stays visible on the regulated presets
    // that pre-select it, but it never reaches the total: it is sold through a conversation,
    // not billed on signup for a capability the product cannot switch on yet.
    lines.push({
      key: `addon:${addonId}`,
      label: addon.name,
      detail: addon.onRequest
        ? 'On request — contact sales'
        : isBundled
          ? `Included with ${plan.name}`
          : undefined,
      amount: addon.onRequest || isBundled ? 0 : toLocal(addon.cents),
      kind: 'addon',
    })
  }

  const rawSubtotal = lines.reduce((sum, l) => sum + l.amount, 0)

  // Industry load multiplier: compliance and support cost carried across the whole subscription.
  const loadDelta = rawSubtotal * (industry.loadMultiplier - 1)
  if (Math.round(loadDelta) !== 0) {
    lines.push({
      key: 'industry_load',
      label: `${industry.name} compliance and support load`,
      detail: `${Math.round((industry.loadMultiplier - 1) * 100)}% applied to subscription`,
      amount: loadDelta,
      kind: 'adjustment',
    })
  }

  const subtotalRaw = rawSubtotal + loadDelta
  const subtotal = roundToGranularity(subtotalRaw, locale.roundTo, locale.minorUnits)

  const discount =
    input.billingCycle === 'annual'
      ? roundToGranularity(subtotal * ANNUAL_DISCOUNT, locale.roundTo, locale.minorUnits)
      : 0

  const afterDiscount = subtotal - discount
  // Tax rounds to a whole major unit so the total stays a clean figure rather than
  // trailing fractional minor units that read as a rounding artefact on an invoice.
  const tax =
    Math.round((afterDiscount * locale.taxRate) / locale.minorUnits) * locale.minorUnits
  const total = afterDiscount + tax

  if (plan.contactSales) {
    warnings.push(
      'Enterprise pricing is indicative. Final terms are agreed with sales and may include custom modules.',
    )
  }

  const monthlyBaseline = roundToGranularity(subtotalRaw, locale.roundTo, locale.minorUnits)
  const annualDiscountAmount = roundToGranularity(
    monthlyBaseline * ANNUAL_DISCOUNT,
    locale.roundTo,
    locale.minorUnits,
  )

  return {
    input,
    currency: locale.currency,
    currencySymbol: locale.currencySymbol,
    minorUnits: locale.minorUnits,
    lines: lines.map((l) => ({ ...l, amount: Math.round(l.amount) })),
    subtotal,
    discount,
    tax,
    taxLabel: locale.taxLabel,
    total,
    monthlyEquivalent: afterDiscount,
    annualSavings: annualDiscountAmount * 12,
    warnings,
  }
}

export function formatMoney(
  minorAmount: number,
  currency: string,
  minorUnits: number,
  localeTag = 'en-US',
): string {
  const value = minorAmount / minorUnits
  // Whole amounts drop the decimals entirely; fractional ones always show both places,
  // so a column of prices never mixes "1,200" with "95,674.4".
  const fractionDigits = Math.abs(value % 1) < 0.005 ? 0 : 2
  try {
    return new Intl.NumberFormat(localeTag, {
      style: 'currency',
      currency,
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(value)
  } catch {
    return `${currency} ${value.toFixed(2)}`
  }
}

/** Builds the default quote input for an industry, used to seed onboarding and pricing pages. */
export function presetInputFor(
  industryId: IndustryId,
  localeId: QuoteInput['localeId'],
  billingCycle: QuoteInput['billingCycle'] = 'monthly',
): QuoteInput {
  const industry = getIndustry(industryId)!
  return {
    planId: industry.presets.plan,
    industryId,
    localeId,
    billingCycle,
    seats: industry.presets.seats,
    numbers: industry.presets.numbers,
    conversations: industry.presets.conversations,
    addons: [...industry.presets.addons],
  }
}
